import type { JoinLayoutProps } from "./types";

// ─── Asset Strategy ───────────────────────────────────────────────────────
// Pexels/Mixkit/Coverr CDN'leri hotlink koruması yüzünden 403 dönüyor.
// Çözüm:
//   • Görseller → Unsplash (hotlink-friendly, kesin çalışır)
//   • Video sloları → apps/web/public/demo/*.mp4 (self-hosted, kesin çalışır)
//   • Scroll-driven frame'ler → public/demo/frames/{theme}/{i}.jpg
//     (`scripts/extract-demo-frames.sh` ile üretilir; yoksa runtime fallback
//      `useScrollFrameSequence` videoyu kendisi extract eder)
//
// Brief: docs/research/2026-05-17-join-demos-creative-brief.md

export const DEMO_VIDEOS = {
  // Hero ambient loop (autoplay, NOT scrubbed — fine as <video>)
  // AV1 first for modern browsers (~30% smaller), H.264 fallback.
  heroLoop: "/demo/cinematic-hero.mp4",
  heroLoopAv1: "/demo/cinematic-hero.av1.mp4",

  // Source for the runtime frame-sequence fallback. The build script extracts
  // JPGs from this into /demo/frames/{theme}/. NEVER scrubbed via
  // `video.currentTime` anymore.
  timelineScroll: "/demo/cinematic-timeline.mp4",
};

// 48 frames per scroll-driven section. Must match `extract-demo-frames.sh`.
export const DEMO_FRAME_COUNT = 48;

/**
 * URL prefix for build-time extracted JPG frames per theme.
 * Hook reads `${prefix}/{i}.jpg` for i in [0, DEMO_FRAME_COUNT).
 * If the JPG files don't exist (script not run), the hook falls back to
 * runtime video extraction.
 */
export const DEMO_FRAMES = {
  cinematic: "/demo/frames/cinematic",
  mirage: "/demo/frames/mirage",
  prism: "/demo/frames/prism",
  veil: "/demo/frames/veil",
} as const;

// Unsplash CDN — hotlink-friendly, responsive parametreli.
const u = (id: string, w = 1920) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;

export const DEMO_IMAGES = {
  // Cinematic hero için fallback (video yüklenemezse)
  hero: u("1519741497674-611481863552", 1920),     // dramatic outdoor
  heroAlt: u("1606800052052-a08af7148866", 1920),   // floral arch

  // Minimal/Editorial portré
  ring: u("1600585154340-be6161a56a0c", 1200),      // ring close-up
  story: u("1525258946800-98cfd641d0de", 1600),     // couple portrait
  venue: u("1519225421980-715cb0215aed", 1920),     // venue exterior

  // Galeri (5 görsel — tüm demolar paylaşır)
  gallery: [
    u("1519741497674-611481863552", 800),
    u("1469371670807-013ccf25f16a", 800),  // ← replaced (was 404)
    u("1606800052052-a08af7148866", 800),
    u("1583939003579-730e3918a45a", 800),
    u("1519225421980-715cb0215aed", 800),
  ],
};

/**
 * Demo önizleme verisi. `formSlot` BİLEREK yok — hangi formun render edileceği
 * çağıranın kararı (`demo.tsx` → DemoFormStub, gerçek sayfa → JoinRegisterForm),
 * mock veriye gömülürse stub yeniden gerçek düğünlere sızabilir.
 */
export const MOCK_WEDDING: Omit<JoinLayoutProps, "formSlot"> = {
  title: "Deniz & Arda",
  dateTime: "2026-08-23T19:00:00",
  location: "Çırağan Palace Kempinski, Beşiktaş, İstanbul",
  // Kına düğünden ÖNCE — temalar sıra varsayımı yapmasın diye demo da böyle.
  program: [
    {
      key: "event2",
      name: "Kına",
      dateTime: "2026-08-16T19:00:00",
      dateLabel: "16 Ağustos 2026",
      numericDateLabel: "16.08.2026",
      dayMonthLabel: "16 Ağustos",
      weekdayLabel: "Pazar",
      timeLabel: "19:00",
      location: "Adile Sultan Kasrı, Üsküdar, İstanbul",
      locationShort: "Adile Sultan Kasrı",
      cityLabel: "İstanbul",
      daysUntil: 12,
    },
    {
      key: "primary",
      name: "Düğün",
      dateTime: "2026-08-23T19:00:00",
      dateLabel: "23 Ağustos 2026",
      numericDateLabel: "23.08.2026",
      dayMonthLabel: "23 Ağustos",
      weekdayLabel: "Pazar",
      timeLabel: "19:00",
      location: "Çırağan Palace Kempinski, Beşiktaş, İstanbul",
      locationShort: "Çırağan Palace Kempinski",
      cityLabel: "İstanbul",
      daysUntil: 19,
    },
  ],
  description:
    "İki yıl önce bir yaz akşamı tanıştık. O gün hiç fark etmeden hayatımızın en güzel sayfası açıldı. Sevgimizi sevinizle taçlandırmak için sizi düğünümüze davet etmekten mutluluk duyuyoruz.",
  slug: "demo",
  coverPhotoUrl: DEMO_IMAGES.hero,
  galleryImages: DEMO_IMAGES.gallery,
  // Demo-only ambient loop so layouts that support a video background (Onyx
  // Venue) can be previewed. Real weddings leave this undefined → image + Ken Burns.
  ambientVideoUrl: DEMO_VIDEOS.heroLoop,
};
